import { createFitnessId } from '@/lib/fitnessId';
import { FITNESS_DRAFT_RESTORE_MAX_BYTES, parseFitnessDraftRestoreRaw } from '@/lib/fitnessDraftRestoreCodec';

const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);
const invalid = (): never => { throw new Error('invalid_fitness_draft_restore'); };
/** Only the pre-client-ID browser draft and ISO millisecond spelling migrate.
 * The complete strict restore codec remains the final acceptance boundary. */
export function migrateFitnessLegacyDraftRaw(raw: string, ownerId: string, clientIdHint?: string): string {
  try { parseFitnessDraftRestoreRaw(raw, ownerId); return raw; } catch { /* Inspect only bounded JSON for the supported legacy shape. */ }
  if (raw.length > FITNESS_DRAFT_RESTORE_MAX_BYTES || new TextEncoder().encode(raw).byteLength > FITNESS_DRAFT_RESTORE_MAX_BYTES) return invalid();
  let draft: Record<string, unknown>;
  try { draft = JSON.parse(raw) as Record<string, unknown>; } catch { return invalid(); }
  if (!draft || typeof draft !== 'object' || Array.isArray(draft) || draft.version !== 1 || draft.userId !== ownerId) return invalid();
  let changed = false;
  if (!Object.prototype.hasOwnProperty.call(draft, 'clientId') || draft.clientId === null || draft.clientId === '') {
    // Older drafts can already name their owned server session. Preserve that
    // identity; otherwise create one UUID using the existing crypto fallback.
    if (clientIdHint !== undefined && !uuid(clientIdHint)) return invalid();
    draft.clientId = uuid(draft.sessionId) ? draft.sessionId : clientIdHint ?? createFitnessId(); changed = true;
  }
  const canonicalTimestamp = (value: unknown): unknown => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value) || !Number.isFinite(Date.parse(value))) return value;
    const canonical = new Date(value).toISOString();
    const spelling = value.replace(/(?:\.(\d{1,3}))?Z$/, (_match, fraction: string | undefined) => '.' + (fraction ?? '').padEnd(3, '0') + 'Z');
    if (canonical !== spelling) return value; // Do not repair an impossible date.
    if (canonical !== value) changed = true; return canonical;
  };
  draft.startedAt = canonicalTimestamp(draft.startedAt);
  if (Array.isArray(draft.exercises)) for (const exercise of draft.exercises) {
    if (exercise && typeof exercise === 'object' && Array.isArray(exercise.sets)) for (const set of exercise.sets) {
      if (set && typeof set === 'object' && Object.prototype.hasOwnProperty.call(set, 'completedAt')) set.completedAt = canonicalTimestamp(set.completedAt);
    }
  }
  if (!changed) return invalid();
  const migrated = JSON.stringify(draft); parseFitnessDraftRestoreRaw(migrated, ownerId); return migrated;
}
